import {useCallback} from 'react'

import {PreviewQueryProvider} from '../../contexts/PreviewQueryContext'
import {useConfirmDiscardChanges} from '../../hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '../../hooks/use-saved-view-editor'
import type {
  SavedView,
  SavedViewIssuesRenderer,
  SavedViewSaveAwareContent,
  UpdateSavedViewResponse,
  UpdateSavedViewVariables,
  UserShortcut,
} from '../../types'
import styles from './RepositorySavedViewPage.module.css'
import {SavedViewEditHeader} from './SavedViewEditHeader'

function toShortcut(view: SavedView): UserShortcut {
  return {
    id: String(view.id),
    name: view.name,
    searchType: 'issues',
    query: view.query,
    description: view.description ?? '',
    icon: view.icon,
    color: view.color,
  }
}

interface SavedViewEditViewProps<TConfiguration> {
  savedView: SavedView
  owner: string
  repo: string
  onSaved: (response: UpdateSavedViewResponse<TConfiguration>, sent: UpdateSavedViewVariables<TConfiguration>) => void
  onCancel: () => void
  IssuesRenderer?: SavedViewIssuesRenderer
  /** Host controls for what `configuration` holds. */
  configurationFields?: SavedViewSaveAwareContent
  /** Host-owned configuration saved with the edit as-is. Absent sends none. */
  configuration?: TConfiguration
  /** Whether the options in `configurationFields` changed since the form opened. Counts as an unsaved change. */
  configurationChanged?: boolean
}

export function SavedViewEditView<TConfiguration = never>({
  savedView,
  owner,
  repo,
  onSaved,
  onCancel,
  IssuesRenderer,
  configurationFields,
  configuration,
  configurationChanged = false,
}: SavedViewEditViewProps<TConfiguration>) {
  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue, isDirty} =
    useSavedViewEditor({
      initialShortcut: () => toShortcut(savedView),
    })

  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty: isDirty || configurationChanged})

  const handleCancel = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      onCancel()
    }
  }, [confirmDiscardIfDirty, onCancel])

  return (
    <>
      <SavedViewEditHeader<TConfiguration>
        savedView={savedView}
        owner={owner}
        repo={repo}
        shortcut={shortcut}
        setShortcutValue={setShortcutValue}
        onPreviewQuerySubmit={commitPreviewQuery}
        onSaved={onSaved}
        onCancel={handleCancel}
        configurationFields={configurationFields}
        configuration={configuration}
      />
      {IssuesRenderer ? (
        <div className={styles.middlePaneGrid}>
          <PreviewQueryProvider value={previewQueryContextValue}>
            <IssuesRenderer owner={owner} repo={repo} query={previewQuery} preheatEnabled={false} />
          </PreviewQueryProvider>
        </div>
      ) : null}
    </>
  )
}
